<script lang="ts">
	import { ui } from '#library/stores.ts'
	import { page } from '$app/state'
	import type { Template } from '#library/types.ts'
	import { fade, fly, slide } from 'svelte/transition'
	import { SvelteMap } from 'svelte/reactivity'
	import { goto } from '$app/navigation'

	let query = $state('')
	let form: HTMLFormElement | undefined = $state(undefined)
	let results_map = new SvelteMap<string, Array<Template>>()
	let input: HTMLInputElement | undefined = $state(undefined)

	const result_types = new Map([
		['page', 'Pages'],
		['news', 'News'],
		['blog', 'Blog'],
		['subject', 'Subject'],
		['member', 'Member']
	])

	const result_headings = $derived.by(() => {
		if (!results_map.size) return []
		return Array.from(result_types.keys()).filter(value => results_map.has(value))
	})

	let search_state: 'idle' | 'searching' | 'no-results' = $state('idle')

	const { api, version } = $derived(page.data)
	const { popular_searches } = $derived(page.data.layout)

	function search(e: MouseEvent) {
		query = (e.target as HTMLButtonElement).textContent || ''
		form?.requestSubmit()
	}

	async function onsubmit(e: SubmitEvent) {
		e.preventDefault()

		if (query.trim() === '') return clear()
		search_state = 'searching'

		results_map.clear()

		let stories: Array<Template> = []
		let pagination: { total: number | undefined; page: number } = {
			total: undefined,
			page: 1
		}

		while (pagination.total === undefined || pagination.total > stories.length) {
			const response = await api.get('cdn/stories', {
				version,
				per_page: 100,
				page: pagination.page,
				search_term: query.trim(),
				resolve_links: 'url'
			})

			stories.push(...response.data.stories)
			pagination.total = Number(response.total)
			pagination.page++
		}

		for (const item of stories) {
			const type = item.content.component!

			// Only add types that are in the result_types map
			if (!result_types.has(type)) continue

			if (!results_map.has(type)) results_map.set(type, [item])
			else results_map.get(type)!.push(item)
		}

		if (results_map.size === 0) {
			search_state = 'no-results'
			return
		}

		// Set the hash to the first result type
		goto(with_hash(result_headings?.[0]), { replace: true, reset: false })

		search_state = 'idle'
	}

	/** This page with another hash, or none; `page.url` itself is readonly */
	const with_hash = (hash?: string) => `${page.url.pathname}${page.url.search}${hash ? `#${hash}` : ''}`

	function clear() {
		query = ''
		results_map.clear()
		goto(with_hash(), { replace: true, reset: false })
		search_state = 'idle'
	}

	function close() {
		$ui.search = false
		clear()
	}

	const target = $derived(page.url.hash.slice(1))

	function path(result: Template) {
		return result.full_slug.replace(result.slug, '').split('/').filter(Boolean).join(' / ') + ' / '
	}

	// afterNavigate(({ type }) => type === 'link' && close())

	const nano = $derived(['enquire', 'prospectus'].includes(page.data.story?.content.component ?? ''))
</script>

{#if $ui.search && !nano}
	<div class="search" transition:fly={{ y: -100 }} onintroend={() => input?.focus()}>
		<form class="form" bind:this={form} {onsubmit}>
			<div class="field" in:fade={{ delay: 100 }}>
				<svg class="svg" class:spinner={search_state === 'searching'}>
					<use xlink:href="/images/vector.svg#{search_state === 'searching' ? 'spinner' : 'search'}"></use>
				</svg>
				<input
					class="input"
					name="search"
					placeholder="Search for pages, people or info"
					bind:value={query}
					bind:this={input}
					in:fade={{ delay: 200 }}
				/>
				{#if query.length}
					<button class="clear" type="button" onclick={clear} transition:fly={{ x: -20 }}>Clear</button>
				{/if}
			</div>
			<button class="close" aria-label="Close" type="button" onclick={close}>
				<svg class="svg">
					<use xlink:href="/images/vector.svg#close"></use>
				</svg>
			</button>
		</form>
		<div class="results">
			{#if search_state === 'no-results'}
				<p class="empty" transition:slide>No results found.</p>
			{:else if result_headings?.length}
				<nav class="counts">
					{#each result_headings as heading, i (i)}
						{@const results = results_map.get(heading)}
						{#if results?.length}
							<a class="count" href="#{heading}" class:active={target === heading} onclick={close}>
								{result_types.get(heading)} ({results.length})
							</a>
						{/if}
					{/each}
				</nav>
				{#if results_map?.has(target)}
					{#each results_map.get(target)! as result, i (i)}
						<div class="result" in:fly|global={{ delay: 300 + i * 60, x: 40 }}>
							<div class="path">{path(result)}</div>
							<a class="title" href="/{result.full_slug}" onclick={close}>
								{result.name}
								<svg class="svg">
									<use xlink:href="/images/vector.svg#arrow-up-right"></use>
								</svg>
							</a>
						</div>
					{/each}
				{/if}
			{/if}
			{#if results_map.size === 0 && popular_searches}
				<div class="suptitle">Popular Searches</div>
				{#each popular_searches.trim().split('\n') as search_term, i (i)}
					<button class="title popular" onclick={search} in:fly|global={{ delay: 300 + i * 30, x: 40 }}>
						{search_term}
					</button>
				{/each}
			{/if}
		</div>
	</div>
{/if}

<style lang="css">
	.search {
		background-color: var(--c-lupton);
		color: var(--c-white);
		inset: 0;
		overflow-y: auto;
		overscroll-behavior: contain;
		padding: 2.25rem 6vw 4rem;
		position: fixed;
		z-index: 10;
	}

	.form {
		align-items: start;
		display: flex;
		gap: 2.5rem;
	}

	.field {
		align-items: center;
		border-bottom: 2px solid var(--c-white);
		display: flex;
		flex: 1 1 auto;
		gap: 2rem;
		padding-bottom: 1rem;
	}

	.input {
		background-color: transparent;
		border: none;
		color: var(--c-white);
		font: 400 var(--f-h5);
		letter-spacing: var(--ls-h5);
		width: 100%;

		&::placeholder {
			color: hsl(0 0% 100% / 0.3);
		}

		&:focus {
			outline: none;
		}
	}

	.svg {
		flex-shrink: 0;
		size: 2rem;

		@media (--from-mobile) {
			size: 2.5rem;
		}

		&.spinner {
			animation: spin 1s linear infinite;
		}
	}

	.close {
		margin-block-start: 0.25rem;
		size: 2rem;
	}

	.results {
		align-items: start;
		display: flex;
		flex-direction: column;
		padding-block-start: 4rem;
	}

	.result {
		align-self: stretch;
		border-bottom: 2px solid var(--c-white);
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
		padding-block: 2rem;
	}

	.suptitle {
		font: 400 var(--f-body2);
		letter-spacing: var(--ls-body2);
		margin-block-end: 1.25rem;
		text-transform: uppercase;
	}

	.title {
		align-items: center;
		display: flex;
		font: 400 var(--f-h5);
		flex: 1 1 auto;
		justify-content: space-between;
		letter-spacing: var(--ls-h5);
		transition: translate 0.3s ease;

		&:is(.popular) {
			margin-block-end: 1rem;
		}

		&:hover {
			opacity: 0.7;
		}
	}

	.counts {
		align-items: center;
		display: flex;
		flex-flow: wrap;
		gap: 1rem 3rem;
		font: 600 var(--f-h4);
		letter-spacing: var(--ls-h5);
	}

	.count {
		transition: color 0.2s ease-in-out;

		&:is(.active) {
			color: var(--c-pink);
		}
	}

	.path {
		flex: 1 1 100%;
		font: 400 var(--f-signpost);
		letter-spacing: var(--ls-signpost);
		opacity: 0.7;
		text-transform: uppercase;
		margin-block-end: 1rem;
	}

	.empty {
		font: 400 var(--f-h5);
		letter-spacing: var(--ls-h5);
		margin-block-end: 2rem;
	}
</style>
